/**
 * GSAP-smoothed sticky header (design-refs/header-spec.md).
 *
 * Horizon's header.js drives the scroll-up state machine via
 * data-sticky-state on <header-component>:
 *   inactive -> settled in flow at the top of the page
 *   active   -> fixed, revealed while scrolling up
 *   idle     -> hidden while scrolling down
 *
 * This module slides the header instead of stock's opacity fade:
 *   - scrolling down from the top: no tween, the header scrolls away
 *     naturally with the page
 *   - active: slide in from above
 *   - active -> idle: stay pinned (sc-unsticking class) while sliding out,
 *     then unpin and clear transforms
 *
 * Progressive enhancement: data-gsap-sticky gates the CSS overrides, so
 * without GSAP stock behavior remains. Reduced motion keeps stock instant
 * behavior.
 */
(function () {
  if (!window.gsap) return;

  const header = document.querySelector('header-component[sticky="scroll-up"]');
  if (!header) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reducedMotion.matches) return;

  header.setAttribute('data-gsap-sticky', '');

  let lastState = header.dataset.stickyState || 'inactive';

  const observer = new MutationObserver(() => {
    const state = header.dataset.stickyState || 'inactive';
    if (state === lastState) return;
    const cameFrom = lastState;
    lastState = state;

    if (state === 'active') {
      header.classList.remove('sc-unsticking');
      gsap.fromTo(
        header,
        { yPercent: -100 },
        {
          yPercent: 0,
          duration: 0.45,
          ease: 'power3.out',
          overwrite: 'auto',
          // GSAP's yPercent:0 leaves a residual transform on the header, which
          // promotes it to its own compositing layer and severs the glass
          // pill's backdrop-filter from the video behind it (blur stops
          // rendering). Clear the transform once the reveal settles so the
          // resting active state matches top-of-page, where the blur works.
          onComplete: () => gsap.set(header, { clearProps: 'transform' }),
        }
      );
    } else if (state === 'idle' && cameFrom === 'active') {
      // was sticky and visible: keep pinned while sliding out
      header.classList.add('sc-unsticking');
      gsap.to(header, {
        yPercent: -100,
        duration: 0.35,
        ease: 'power2.in',
        overwrite: 'auto',
        onComplete: () => {
          header.classList.remove('sc-unsticking');
          gsap.set(header, { clearProps: 'transform' });
        },
      });
    } else {
      // back at the top, or scrolled away naturally: no motion needed
      header.classList.remove('sc-unsticking');
      gsap.killTweensOf(header);
      gsap.set(header, { clearProps: 'transform' });
    }
  });

  observer.observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });
})();
